import type { RouteObject } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";
import { Spinner } from "@/components/ui/v2/spinner";
import { redirect } from "react-router-dom";
import {
  CompanyRecruitment,
  CompanyRecruitmentActivity,
  CompanyRecruitmentAnalysis,
  CompanyRecruitmentCompare,
  CompanyRecruitmentAiScorecard,
  CompanyRecruitmentAiJob,
  CompanyRecruitmentCreateJob,
  CompanyRecruitmentAssessmentForm,
  CompanyRecruitmentAssessmentNew,
  CompanyRecruitmentAssessmentsTab,
  CompanyRecruitmentCandidatesTab,
  CompanyRecruitmentDashboardTab,
  CompanyRecruitmentInterviewsTab,
  CompanyRecruitmentJobsTab,
  CompanyRecruitmentOffersTab,
  CompanyRecruitmentOfferDetail,
  CompanyRecruitmentOfferForm,
  CompanyRecruitmentCandidateProfile,
  CompanyRecruitmentJobForm,
  CompanyRecruitmentPipeline,
  CompanyRecruitmentScheduleInterview,
  CompanyRecruitmentSettingsPage,
  CompanyRecruitmentTalentPoolTab,
} from "@/utils/lazyLoadComponents";

export enum COMPANY_RECRUITMENT_PATH_SEGMENTS {
  ROOT = "recruitment",
  // Tabs are real routes, not `?hTab=` params: the sidebar renders them as
  // child links from the same declaration the tab bar uses, and a URL that
  // names its tab can be linked to and bookmarked.
  DASHBOARD = "dashboard",
  JOBS = "jobs",
  CANDIDATES = "candidates",
  INTERVIEWS = "interviews",
  ASSESSMENTS = "assessments",
  OFFERS = "offers",
  TALENT_POOL = "talent-pool",
  JOB_FORM = "job-form",
  AI_JOB = "ai-job",
  CREATE_JOB = "create-job",
  PIPELINE = "pipeline",
  ANALYSIS = "analysis",
  COMPARE = "compare",
  CANDIDATE = "candidate",
  AI_SCORECARD = "ai-scorecard",
  ASSESSMENT_FORM = "assessment-form",
  ASSESSMENT_NEW = "assessment-new",
  SCHEDULE_INTERVIEW = "schedule-interview",
  OFFER = "offer",
  OFFER_FORM = "offer-form",
  ACTIVITY = "activity",
  // Module settings (moved out of Company Settings). Literal, not
  // `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum members
  // initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
}

/**
 * Detail page → its tab, for the redirect table below. Kept here, beside the
 * segments, so a new nested page is one line in each place.
 */
const DETAIL_PARENT_TAB: Record<string, COMPANY_RECRUITMENT_PATH_SEGMENTS> = {
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.JOB_FORM]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.AI_JOB]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.CREATE_JOB]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.PIPELINE]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.ANALYSIS]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATE]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATES,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.COMPARE]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATES,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.AI_SCORECARD]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.INTERVIEWS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.SCHEDULE_INTERVIEW]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.INTERVIEWS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENT_FORM]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENTS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENT_NEW]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENTS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFER]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFERS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFER_FORM]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFERS,
  [COMPANY_RECRUITMENT_PATH_SEGMENTS.ACTIVITY]:
    COMPANY_RECRUITMENT_PATH_SEGMENTS.DASHBOARD,
};

const LEGACY_DETAIL_REDIRECTS = Object.entries(DETAIL_PARENT_TAB).map(
  ([leaf, tab]) => ({
    from: leaf,
    to: `/company/${COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT}/${tab}/${leaf}`,
  }),
);

export const companyRecruitmentRoutes: RouteObject = {
  path: COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Recruitment" },
  HydrateFallback: Spinner,
  children: [
    // ── Tab shell: the strip plus whichever tab route is active ──────────────
    {
      Component: CompanyRecruitment,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_RECRUITMENT_PATHS.TAB_DASHBOARD),
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.DASHBOARD,
          handle: { breadcrumb: "Dashboard" },
          Component: CompanyRecruitmentDashboardTab,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
          handle: { breadcrumb: "Job Openings" },
          Component: CompanyRecruitmentJobsTab,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATES,
          handle: { breadcrumb: "Candidates" },
          Component: CompanyRecruitmentCandidatesTab,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.INTERVIEWS,
          handle: { breadcrumb: "Interviews" },
          Component: CompanyRecruitmentInterviewsTab,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENTS,
          handle: { breadcrumb: "Assessments" },
          Component: CompanyRecruitmentAssessmentsTab,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFERS,
          handle: { breadcrumb: "Offers" },
          Component: CompanyRecruitmentOffersTab,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.TALENT_POOL,
          handle: { breadcrumb: "Talent Pool" },
          Component: CompanyRecruitmentTalentPoolTab,
        },
      ],
    },
    // ── Detail pages: nested under the tab they open from, as the Job&IQ
    // portal does (job-openings/[id], candidates/[id]). The URL then says which
    // tab owns the page, so the sidebar and the breadcrumb follow it without a
    // list of exceptions. They sit beside the tab shell, not inside it, so none
    // of them shows the tab strip.
    {
      path: COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
      handle: { breadcrumb: "Job Openings" },
      children: [
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.JOB_FORM,
          // Reads "Create Job" like the source design. One route serves create
          // and edit (edit carries `?id=`), and the crumb has no access to the
          // query — the card header states which mode you're in.
          handle: { breadcrumb: "Create Job" },
          Component: CompanyRecruitmentJobForm,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.CREATE_JOB,
          handle: { breadcrumb: "Create job" },
          Component: CompanyRecruitmentCreateJob,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.AI_JOB,
          handle: { breadcrumb: "Start with AI" },
          Component: CompanyRecruitmentAiJob,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.PIPELINE,
          handle: { breadcrumb: "Pipeline" },
          Component: CompanyRecruitmentPipeline,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.ANALYSIS,
          handle: { breadcrumb: "Candidate analysis" },
          Component: CompanyRecruitmentAnalysis,
        },
      ],
    },
    {
      path: COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATES,
      handle: { breadcrumb: "Candidates" },
      children: [
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATE,
          handle: { breadcrumb: "Candidate" },
          Component: CompanyRecruitmentCandidateProfile,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.COMPARE,
          handle: { breadcrumb: "Compare candidates" },
          Component: CompanyRecruitmentCompare,
        },
      ],
    },
    {
      path: COMPANY_RECRUITMENT_PATH_SEGMENTS.INTERVIEWS,
      handle: { breadcrumb: "Interviews" },
      children: [
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.AI_SCORECARD,
          handle: { breadcrumb: "AI Interview Scorecard" },
          Component: CompanyRecruitmentAiScorecard,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.SCHEDULE_INTERVIEW,
          handle: { breadcrumb: "Schedule interview" },
          Component: CompanyRecruitmentScheduleInterview,
        },
      ],
    },
    {
      path: COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENTS,
      handle: { breadcrumb: "Assessments" },
      children: [
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENT_FORM,
          handle: { breadcrumb: "Assessment" },
          Component: CompanyRecruitmentAssessmentForm,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENT_NEW,
          handle: { breadcrumb: "New assessment" },
          Component: CompanyRecruitmentAssessmentNew,
        },
      ],
    },
    {
      path: COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFERS,
      handle: { breadcrumb: "Offers" },
      children: [
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFER,
          handle: { breadcrumb: "Offer" },
          Component: CompanyRecruitmentOfferDetail,
        },
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFER_FORM,
          handle: { breadcrumb: "Make an offer" },
          Component: CompanyRecruitmentOfferForm,
        },
      ],
    },
    {
      path: COMPANY_RECRUITMENT_PATH_SEGMENTS.DASHBOARD,
      handle: { breadcrumb: "Dashboard" },
      children: [
        {
          path: COMPANY_RECRUITMENT_PATH_SEGMENTS.ACTIVITY,
          handle: { breadcrumb: "Activity" },
          Component: CompanyRecruitmentActivity,
        },
      ],
    },
    // The detail pages' old, un-nested URLs — bookmarks, emailed links and
    // anything still open in a tab — land on the new ones with their query.
    ...LEGACY_DETAIL_REDIRECTS.map(({ from, to }) => ({
      path: from,
      loader: ({ request }: { request: Request }) =>
        redirect(`${to}${new URL(request.url).search}`),
    })),
    // Module settings — a plain sibling route; nothing else here claims the
    // static "settings" segment.
    {
      path: COMPANY_RECRUITMENT_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Recruitment Settings" },
      Component: CompanyRecruitmentSettingsPage,
    },
  ],
};

const _RECRUITMENT_ROOT = withCompanyPrefix(
  addSlashes(COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT),
);
const _tab = (segment: string) =>
  withCompanyPrefix(
    addSlashes(COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT, segment),
  );
const _JOB_FORM_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOB_FORM,
  ),
);
const _AI_JOB_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.AI_JOB,
  ),
);
const _CREATE_JOB_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.CREATE_JOB,
  ),
);
const _ASSESSMENT_NEW_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENTS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENT_NEW,
  ),
);
const _PIPELINE_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.PIPELINE,
  ),
);
const _ANALYSIS_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ANALYSIS,
  ),
);
const _COMPARE_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATES,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.COMPARE,
  ),
);
const _CANDIDATE_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATES,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATE,
  ),
);
const _OFFER_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFERS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFER,
  ),
);
const _ASSESSMENT_FORM_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENTS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENT_FORM,
  ),
);
const _SCHEDULE_INTERVIEW_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.INTERVIEWS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.SCHEDULE_INTERVIEW,
  ),
);
const _AI_SCORECARD_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.INTERVIEWS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.AI_SCORECARD,
  ),
);
const _OFFER_FORM_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFERS,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFER_FORM,
  ),
);
const _ACTIVITY_ROOT = withCompanyPrefix(
  addSlashes(
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ROOT,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.DASHBOARD,
    COMPANY_RECRUITMENT_PATH_SEGMENTS.ACTIVITY,
  ),
);

// Tabs are path segments, declared once here and read by both the tab bar and
// the sidebar via `MODULE_NAV_TABS` (config/nav-tabs.config.ts).
// JOB_FORM_EDIT / PIPELINE / CANDIDATE mirror routes.onboarding.ts's EMPLOYEE
// pattern — a specific record is `?id=`, not a path segment. CANDIDATE's `id`
// is the *application* id, not the candidate's own id — a candidate is scoped
// to one job's pipeline here.
export const COMPANY_RECRUITMENT_PATHS = Object.freeze({
  ROOT: _RECRUITMENT_ROOT,
  TAB_DASHBOARD: _tab(COMPANY_RECRUITMENT_PATH_SEGMENTS.DASHBOARD),
  TAB_JOBS: _tab(COMPANY_RECRUITMENT_PATH_SEGMENTS.JOBS),
  TAB_CANDIDATES: _tab(COMPANY_RECRUITMENT_PATH_SEGMENTS.CANDIDATES),
  TAB_INTERVIEWS: _tab(COMPANY_RECRUITMENT_PATH_SEGMENTS.INTERVIEWS),
  TAB_OFFERS: _tab(COMPANY_RECRUITMENT_PATH_SEGMENTS.OFFERS),
  TAB_ASSESSMENTS: _tab(COMPANY_RECRUITMENT_PATH_SEGMENTS.ASSESSMENTS),
  TAB_TALENT_POOL: _tab(COMPANY_RECRUITMENT_PATH_SEGMENTS.TALENT_POOL),
  JOB_FORM_NEW: _JOB_FORM_ROOT,
  JOB_FORM_EDIT: (id: number | string) => `${_JOB_FORM_ROOT}?id=${id}`,
  /** "Start with AI": the title-and-basics brief that drafts a whole job. */
  AI_JOB: _AI_JOB_ROOT,
  /** Where "Create job" starts: step by step, with AI, or from the library. */
  CREATE_JOB: _CREATE_JOB_ROOT,
  PIPELINE: (id: number | string) => `${_PIPELINE_ROOT}?id=${id}`,
  /** A job's applied and screened applicants, ranked by their stored AI match. `id` is the job. */
  ANALYSIS: (jobId: number | string) => `${_ANALYSIS_ROOT}?id=${jobId}`,
  /**
   * 2–4 applications side by side. `jobId` is where "back" goes (the pipeline
   * they were picked on); without it, back is the Candidates list.
   */
  COMPARE: (applicationIds: number[], jobId?: number | string) =>
    `${_COMPARE_ROOT}?ids=${applicationIds.join(",")}${jobId ? `&job=${jobId}` : ""}`,
  CANDIDATE: (applicationId: number | string) =>
    `${_CANDIDATE_ROOT}?id=${applicationId}`,
  /**
   * The interview details page. `AI_SCORECARD` is the same route under the name
   * the AI rows' button uses — the source design opens one page from both
   * "AI Scorecard" and "View details".
   */
  INTERVIEW_DETAILS: (interviewId: number | string, tab?: string) =>
    `${_AI_SCORECARD_ROOT}?id=${interviewId}${tab ? `&vTab=${tab}` : ""}`,
  AI_SCORECARD: (interviewId: number | string) =>
    `${_AI_SCORECARD_ROOT}?id=${interviewId}`,
  OFFER: (id: number | string) => `${_OFFER_ROOT}?id=${id}`,
  /**
   * The offer form. `applicationId` preselects (and locks) the candidate;
   * `offerId` puts the form in edit mode, loading and re-saving that offer
   * instead of drafting a new one.
   */
  OFFER_FORM: (applicationId?: number | string, offerId?: number | string) => {
    const params = new URLSearchParams();
    if (applicationId) params.set("applicationId", String(applicationId));
    if (offerId) params.set("offerId", String(offerId));
    const query = params.toString();
    return query ? `${_OFFER_FORM_ROOT}?${query}` : _OFFER_FORM_ROOT;
  },
  ACTIVITY: _ACTIVITY_ROOT,
  // `roundId` preselects a round from the job's interview plan, so "Schedule
  // this round" on the candidate profile lands on the right round.
  SCHEDULE_INTERVIEW: (
    applicationId?: number | string,
    roundId?: number | string,
  ) =>
    applicationId
      ? `${_SCHEDULE_INTERVIEW_ROOT}?applicationId=${applicationId}${
          roundId ? `&roundId=${roundId}` : ""
        }`
      : _SCHEDULE_INTERVIEW_ROOT,
  /** "New assessment": choose AI or blank, and the AI brief. */
  ASSESSMENT_NEW: _ASSESSMENT_NEW_ROOT,
  ASSESSMENT_FORM_NEW: _ASSESSMENT_FORM_ROOT,
  ASSESSMENT_FORM_EDIT: (id: number | string) =>
    `${_ASSESSMENT_FORM_ROOT}?id=${id}`,
  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.RECRUITMENT.ROOT,
  SETTINGS_VTAB_SCREENING: MODULE_SETTINGS_PATHS.RECRUITMENT.VTAB_SCREENING,
  SETTINGS_VTAB_MATCH_WEIGHTING:
    MODULE_SETTINGS_PATHS.RECRUITMENT.VTAB_MATCH_WEIGHTING,
  SETTINGS_VTAB_INTERVIEWS: MODULE_SETTINGS_PATHS.RECRUITMENT.VTAB_INTERVIEWS,
  SETTINGS_VTAB_JOB_LIBRARY: MODULE_SETTINGS_PATHS.RECRUITMENT.VTAB_JOB_LIBRARY,
  SETTINGS_VTAB_EMAIL_TEMPLATES:
    MODULE_SETTINGS_PATHS.RECRUITMENT.VTAB_EMAIL_TEMPLATES,
  SETTINGS_VTAB_OFFER_LETTERS:
    MODULE_SETTINGS_PATHS.RECRUITMENT.VTAB_OFFER_LETTERS,
});
